import { getTable } from '../../requests/documentDatas';
import { buildTableQuery } from '../../shared/tables/tableQuery';
import {
  blockColumnsOf, foldCanvasLabels, materializeTable, materializedContentChanged,
} from './webTableMaterialize';

/**
 * Rafraîchissement des **tableaux variables** du site.
 *
 * Un tableau variable est un bloc `EditableDataTable` branché sur une table du
 * document (`tableName`). Son contenu est **dérivé**, comme dans le Proposal :
 * les colonnes et les lignes ne sont pas une saisie mais la matérialisation de
 * la dernière lecture de la table, filtrée par `tableConfig` (colonnes
 * masquées, renommées, réordonnées, largeurs). Le site publié se rend sans
 * backend joignable, d'où la matérialisation dans les props plutôt qu'une
 * lecture à l'affichage.
 *
 * Deux blocs peuvent viser la même table avec des axes donnée différents : la
 * lecture est donc mise en cache sur la table **et** sur ses axes, exactement
 * la clé que `buildTableQuery` fabrique déjà pour l'URL.
 */

export const WEB_TABLE_BLOCK_TYPE = 'EditableDataTable';

/** Le bloc est-il branché sur une table du document ? */
export function isVariableTableBlock(node) {
  return !!node
    && node.type === WEB_TABLE_BLOCK_TYPE
    && typeof node.props?.tableName === 'string'
    && node.props.tableName.trim() !== '';
}

/**
 * Clé de lecture d'une table : le document qui la sert, son nom, ses axes
 * donnée. Le document en fait partie parce qu'une offre et l'une de ses
 * opportunités peuvent servir une table du même nom sans que ce soit la même.
 */
export function tableFetchKey(tableName, tableConfig, tableSource = null, split = false) {
  const scope = tableSource?.id && tableSource?.type
    ? `${tableSource.type}:${tableSource.id}|`
    : '';
  return `${scope}${tableName}${buildTableQuery({ tableName, split, tableConfig })}`;
}

/** Les couples (table, config) cités par une structure, dédoublonnés. */
export function collectTableReadsDeep(value, acc = new Map()) {
  if (Array.isArray(value)) {
    value.forEach((item) => collectTableReadsDeep(item, acc));
    return acc;
  }
  if (value && typeof value === 'object') {
    if (isVariableTableBlock(value)) {
      const {
        tableName, tableConfig = null, tableSource = null, shouldSplit = false,
      } = value.props;
      acc.set(
        tableFetchKey(tableName, tableConfig, tableSource, shouldSplit),
        { tableName, tableConfig, tableSource, split: shouldSplit },
      );
    }
    Object.values(value).forEach((item) => collectTableReadsDeep(item, acc));
  }
  return acc;
}

function refreshDeep(value, tables) {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const refreshed = refreshDeep(item, tables);
      if (refreshed !== item) changed = true;
      return refreshed;
    });
    return changed ? next : value;
  }
  if (!value || typeof value !== 'object') return value;

  if (isVariableTableBlock(value)) {
    const {
      tableName, tableConfig = null, tableSource = null, shouldSplit = false,
    } = value.props;
    const table = tables.get(tableFetchKey(tableName, tableConfig, tableSource, shouldSplit));
    // Table absente du serveur ou illisible : on garde ce que le bloc porte,
    // jamais un tableau vide au client.
    if (!table) return value;

    // Un intitulé renommé au canvas devient un `customLabel` de la config,
    // sinon la relecture — qui recalcule tout — l'effacerait.
    const folded = foldCanvasLabels(tableConfig, blockColumnsOf(value.props));
    const materialized = materializeTable(table, folded, value.props);
    if (!materialized) return value;
    if (folded === tableConfig && !materializedContentChanged(value.props, materialized)) {
      return value;
    }

    return { ...value, props: { ...value.props, tableConfig: folded, ...materialized } };
  }

  let changed = false;
  const next = {};
  Object.entries(value).forEach(([prop, item]) => {
    const refreshed = refreshDeep(item, tables);
    if (refreshed !== item) changed = true;
    next[prop] = refreshed;
  });
  return changed ? next : value;
}

/**
 * Relit toutes les tables citées par la structure et rematérialise les blocs
 * concernés.
 *
 * @param {object} value - structure à parcourir (web_data, pages, squelette)
 * @param {{ id: string|number, type: string }} identity - document source
 * @returns {Promise<object>} la **même référence** si rien n'a changé
 */
export async function refreshVariableTablesDeep(value, identity, { fetchTable = getTable } = {}) {
  if (!identity?.id || !identity?.type) return value;

  const reads = Array.from(collectTableReadsDeep(value).entries());
  if (!reads.length) return value;

  const tables = new Map();
  await Promise.all(reads.map(async ([key, {
    tableName, tableConfig, tableSource, split,
  }]) => {
    try {
      // Un bloc peut viser une table d'une opportunité rattachée : elle se lit
      // sur cette opportunité, pas sur le document ouvert.
      const from = tableSource?.id && tableSource?.type ? tableSource : identity;
      const table = await fetchTable(from, tableName, { split, tableConfig });
      if (table) tables.set(key, table);
    } catch {
      // Une table illisible ne doit pas faire échouer le rafraîchissement des
      // autres : le bloc garde ce qu'il porte.
    }
  }));

  return refreshDeep(value, tables);
}
